<template>
  <div class="flex gap-2">
    <div class="box w900" :class="{ w1200: groupByRole }">
      <div class="box-title flex items-center justify-between">
        <div>Zone to Zone</div>
        <div class="flex items-center gap-2">
          <el-input
            v-model="keyword"
            size="mini"
            class="w-56"
            clearable
            placeholder="min 2 letters"
          >
            <template slot="append">
              <i class="el-icon-search" />
            </template>
          </el-input>
          <el-tooltip
            placement="top"
            :open-delay="500"
            content="Sort Zones"
            v-if="sortable"
          >
            <a @click="sort" title="Sort Zones">
              <i class="fas fa-arrow-down-a-z text-blue-500 text-base" />
            </a>
          </el-tooltip>
          <el-tooltip
            v-else
            placement="top"
            :open-delay="500"
            content="Clear filter and sort"
          >
            <a class="fas fa-arrow-down-a-z text-gray-500 text-base" />
          </el-tooltip>
        </div>
      </div>
      <div class="box-content">
        <table class="tbl">
          <thead>
            <tr>
              <th class="w-6">
                <a @click="add" class="fas fa-plus-circle" />
              </th>
              <th class="w-36">Zone</th>
              <th class="w-36">Zone</th>
              <th class="w-8">
                <el-tooltip
                  placement="top"
                  :open-delay="1000"
                  content="Different price the other direction; Click to filter only zones with different prices."
                >
                  <i
                    class="fas fa-not-equal text-gray-500 text-lg cursor-pointer"
                    :class="{ 'text-red-500': notEqualOn }"
                    @click="notEqualOn = !notEqualOn"
                  />
                </el-tooltip>
              </th>
              <slot v-if="groupByRole">
                <th
                  v-for="(role, abbrev) in driverTypes"
                  :key="abbrev"
                  class="w-20 text-xs tracking-tighter"
                >
                  {{ role }}
                </th>
              </slot>
              <th v-else>Global</th>
            </tr>
          </thead>
          <tbody :key="`zones${identifier}`">
            <tr v-for="(rate, index) in items" :key="index">
              <td>
                <el-popconfirm
                  title="Are you sure to delete this zone rate?"
                  confirmButtonText="Delete"
                  cancelButtonText="Cancel"
                  @confirm="remove(index)"
                >
                  <a
                    class="fas fa-trash-alt"
                    slot="reference"
                    v-on:click.stop
                  />
                </el-popconfirm>
              </td>
              <td class="text-center w-72">
                <div class="el-input-inner-label">
                  <label>
                    <i
                      class="far fa-building text-sm cursor-pointer"
                      :class="{
                        'text-blue-500': rate.z1c,
                        'text-gray-300': !rate.z1c,
                      }"
                      @click="toggleMap(rate, 'z1c')"
                    />
                  </label>
                  <slot v-if="rate.z1c">
                    <el-select
                      v-model="rate.z1"
                      size="mini"
                      class="w-full"
                      placeholder="Select Location"
                      filterable
                      clearable
                    >
                      <slot v-for="group in groupedLocations">
                        <el-option-group
                          v-if="!isEmptyData(group[1])"
                          :key="group[0]"
                          :label="group[0]"
                        >
                          <el-option
                            v-for="item in group[1]"
                            :key="item.id"
                            :label="item.name"
                            :value="item.id"
                          >
                          </el-option>
                        </el-option-group>
                      </slot>
                    </el-select>
                  </slot>
                  <slot v-else>
                    <el-input
                      v-if="rate.z1m"
                      v-model="rate.z1"
                      size="mini"
                      :key="`z1city${index}`"
                      placeholder="City/State"
                      @blur="checkCity(rate, 'z1')"
                    />
                    <map-suggest
                      v-else
                      v-model="rate.z1"
                      :cities="true"
                      :key="`z1map${index}`"
                      :appendBody="true"
                      class="grow"
                    />
                    <i
                      v-if="!rate.z1c"
                      class="fas fa-map-location-dot text-sm cursor-pointer text-blue-500"
                      :class="{ 'text-gray-300': rate.z1m }"
                      @click="toggleMap(rate, 'z1m')"
                    />
                  </slot>
                </div>
              </td>
              <td class="text-center w-72">
                <div class="el-input-inner-label">
                  <label>
                    <i
                      class="far fa-building text-sm cursor-pointer"
                      :class="{
                        'text-blue-500': rate.z2c,
                        'text-gray-300': !rate.z2c,
                      }"
                      @click="toggleMap(rate, 'z2c')"
                    />
                  </label>
                  <slot v-if="rate.z2c">
                    <el-select
                      v-model="rate.z2"
                      size="mini"
                      class="w-full"
                      placeholder="Select Location"
                      filterable
                      clearable
                    >
                      <slot v-for="group in groupedLocations">
                        <el-option-group
                          v-if="!isEmptyData(group[1])"
                          :key="group[0]"
                          :label="group[0]"
                        >
                          <el-option
                            v-for="item in group[1]"
                            :key="item.id"
                            :label="item.name"
                            :value="item.id"
                          >
                          </el-option>
                        </el-option-group>
                      </slot>
                    </el-select>
                  </slot>
                  <slot v-else>
                    <el-input
                      v-if="rate.z2m"
                      v-model="rate.z2"
                      size="mini"
                      :key="`z2city${index}`"
                      placeholder="City/State"
                      @blur="checkCity(rate, 'z2')"
                    />
                    <map-suggest
                      v-else
                      v-model="rate.z2"
                      :cities="true"
                      :key="`z2map${index}`"
                      :appendBody="true"
                      class="grow"
                    />
                    <i
                      v-if="!rate.z2c"
                      class="fas fa-map-location-dot text-sm cursor-pointer text-blue-500"
                      :class="{ 'text-gray-300': rate.z2m }"
                      @click="toggleMap(rate, 'z2m')"
                    />
                  </slot>
                </div>
              </td>
              <td class="text-center">
                <i
                  v-if="!sameEnds(rate)"
                  class="fas fa-not-equal cursor-pointer text-base"
                  :class="rate.opp ? 'text-red-500' : 'text-gray-200'"
                  title="Different price the other direction"
                  @click="setOpp(rate, !rate.opp)"
                />
              </td>
              <slot v-if="groupByRole">
                <td
                  v-for="(role, abbrev) in driverTypes"
                  :key="abbrev"
                  class="text-center"
                >
                  <div
                    class="flex items-center gap-1 p-0.5 justify-center border rounded"
                  >
                    <i v-if="showOpp(rate)" class="fas fa-arrow-right" />
                    <i v-else class="fas fa-right-left" />
                    <el-input-number
                      v-model="rate[abbrev]"
                      size="mini"
                      :precision="2"
                      :step="0.01"
                      :min="0"
                      :controls="false"
                      style="width: 60px"
                    />
                    <slot v-if="showOpp(rate)">
                      <i class="fas fa-arrow-left" />
                      <el-input-number
                        v-model="rate['r' + abbrev]"
                        size="mini"
                        :precision="2"
                        :step="0.01"
                        :min="0"
                        :controls="false"
                        style="width: 60px"
                      />
                    </slot>
                  </div>
                </td>
              </slot>
              <td v-else class="text-center">
                <div
                  class="flex items-center gap-1 p-0.5 justify-center border rounded"
                >
                  <i v-if="showOpp(rate)" class="fas fa-arrow-right" />
                  <i v-else class="fas fa-right-left" />
                  <el-input-number
                    v-model="rate.all"
                    size="mini"
                    :precision="2"
                    :step="0.01"
                    :min="0"
                    :controls="false"
                    style="width: 60px"
                  />

                  <slot v-if="showOpp(rate)">
                    <i class="fas fa-arrow-left" />
                    <el-input-number
                      v-model="rate.rall"
                      size="mini"
                      :precision="2"
                      :step="0.01"
                      :min="0"
                      :controls="false"
                      style="width: 60px"
                    />
                  </slot>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="my-1" v-if="total > pageSize">
          <el-pagination
            background
            layout="prev, pager, next"
            :page-size="pageSize"
            :current-page="page"
            :total="total"
            @current-change="paginate($event)"
          />
        </div>
      </div>
    </div>
    <div class="box w-96 h-40">
      <div class="box-title">Bobtail</div>
      <div class="box-content">
        <div class="flex flex-col gap-2 text-xs">
          <div class="flex items-center gap-2 p-2">
            <div class="inner-label w-40">
              <label>Under</label>
              <el-input-number
                v-model="driver.bobtail.miles"
                size="mini"
                :precision="0"
                :step="1"
                :min="0"
                :controls="false"
              />
              <label>miles</label>
            </div>
            <div class="inner-label w-40">
              <label>Pay $</label>
              <el-input-number
                v-model="driver.bobtail.per_mile"
                size="mini"
                :precision="2"
                :step="0.01"
                :min="0"
                :controls="false"
              />
              <label>per mile</label>
            </div>
          </div>
          <center class="text-red-500">
            A longer distance will apply the zone to zone rate as bobtail fee.
          </center>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: "DriverZones",
  props: {
    driver: {
      type: Object,
      required: true,
    },
    driverTypes: {
      type: Object,
      default: () => ({}),
    },
    options: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      keyword: "",
      notEqualOn: false,
      page: 1,
      total: 0,
      pageSize: 10,
      items: [],
      identifier: 1,
      fuseOptions: {
        shouldSort: true,
        threshold: 0.15,
        ignoreLocation: true,
        ignoreFieldNorm: true,
        minMatchCharLength: 2,
        keys: ["z1", "z2"],
      },
    };
  },
  created() {
    this.ensure();
    this.paginate(1);
  },
  watch: {
    "driver.zonerates": {
      deep: true,
      handler(list) {
        (list || []).forEach((rate) => {
          if (rate.opp && this.sameEnds(rate)) this.setOpp(rate, false);
        });
      },
    },
    keyword() {
      this.paginate(this.page);
    },
    notEqualOn() {
      this.paginate(this.page);
    },
  },
  computed: {
    groupByRole() {
      return this.driver?.group == "role";
    },
    groupedLocations() {
      return [
        ["Terminal", this.options.terminals],
        ["Depot", this.options.depots],
        ["Yard", this.options.yards],
        ["Place", this.options.places],
      ];
    },
    companyNames() {
      const names = {};
      this.groupedLocations.forEach((group) => {
        (group[1] || []).forEach((item) => {
          if (names[item.id] == null) names[item.id] = item.name || "";
        });
      });
      return names;
    },
    sortable() {
      return this.isEmptyData(this.keyword) && !this.notEqualOn;
    },
    filteredRates() {
      let list = this.driver.zonerates || [];
      if (this.notEqualOn) list = list.filter((rate) => this.showOpp(rate));
      const term = (this.keyword || "").trim();
      if (term.length < 2) return list;
      const rows = list.map((rate) => ({
        rate,
        z1: this.zoneName(rate, "z1"),
        z2: this.zoneName(rate, "z2"),
      }));
      const exact = rows.filter(
        (row) => row.z1.trim() == term || row.z2.trim() == term,
      );
      const matched = exact.length
        ? exact
        : this.$search(term, rows, this.fuseOptions).map((o) => o.item);
      return matched.map((row) => row.rate);
    },
  },
  methods: {
    ensure() {
      if (!Array.isArray(this.driver.zonerates)) {
        this.$set(this.driver, "zonerates", []);
        if (!this.driver.group) {
          this.$set(this.driver, "group", "global");
        }
      }
      if (!this.driver.bobtail) {
        this.$set(this.driver, "bobtail", {});
      }
    },
    add() {
      const rate = { z1: "", z2: "", z1c: true, all: 0, bk: 0, cd: 0, oo: 0 };
      Object.keys(this.driverTypes).forEach((abbrev) => {
        if (!rate[abbrev]) rate[abbrev] = 0;
      });
      this.keyword = "";
      this.notEqualOn = false;
      this.driver.zonerates.push(rate);
      this.paginate(-1);
    },
    remove(index) {
      const row = this.items[index];
      const at = this.driver.zonerates.indexOf(row);
      if (at >= 0) this.driver.zonerates.splice(at, 1);
      this.paginate(this.page);
    },
    sort() {
      if (!this.isEmptyData(this.keyword)) return;
      const list = this.driver.zonerates || [];
      const name = (rate, side) => this.zoneName(rate, side).toLowerCase();
      const cmp = (a, b) => {
        if (a === b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return a.localeCompare(b, undefined, { numeric: true });
      };
      list.sort((a, b) => {
        const byZ1 = cmp(name(a, "z1"), name(b, "z1"));
        if (byZ1) return byZ1;
        return cmp(name(a, "z2"), name(b, "z2"));
      });
      this.paginate(this.page);
    },
    paginate(val) {
      this.identifier++;
      const list = this.filteredRates;
      this.total = list.length;
      const groups = this._.chunk(list, this.pageSize);
      const page = val < 0 ? groups.length : Math.min(val, groups.length);
      this.items = page == 0 ? [] : groups[page - 1];
      this.page = Math.max(page, 1);
    },
    toggleMap(rate, field) {
      this.$set(rate, field, !rate[field]);
    },
    checkCity(rate, side) {
      const name = rate[side];
      if (!name) return;
      let [city, state] = name.split(",").map((part) => part.trim());
      if (city && state) {
        city = city
          .split(/\s/)
          .map((part) => part.titleize())
          .join(" ");
        state = state.toUpperCase();
        this.$set(rate, side, [city, state].join(", "));
      }
    },
    zoneKey(rate, side) {
      const value = rate[side];
      if (value === null || value === undefined || value === "") return null;
      return `${rate[`${side}c`] ? "c" : "m"}:${value}`;
    },
    zoneName(rate, side) {
      const value = rate[side];
      if (value === null || value === undefined || value === "") return "";
      if (!rate[`${side}c`]) return String(value);
      return this.companyNames[value] || "";
    },
    sameEnds(rate) {
      const from = this.zoneKey(rate, "z1");
      const to = this.zoneKey(rate, "z2");
      return !!(from && to && from === to);
    },
    showOpp(rate) {
      return !!rate.opp && !this.sameEnds(rate);
    },
    rateKeys() {
      const keys = Object.keys(this.driverTypes || {});
      return keys.includes("all") ? keys : ["all", ...keys];
    },
    setOpp(rate, on) {
      if (on) this.$set(rate, "opp", true);
      else if (Object.prototype.hasOwnProperty.call(rate, "opp"))
        this.$delete(rate, "opp");
      this.rateKeys().forEach((key) => {
        const reverse = `r${key}`;
        if (on) {
          if (rate[reverse] == null) this.$set(rate, reverse, rate[key] || 0);
        } else if (Object.prototype.hasOwnProperty.call(rate, reverse)) {
          this.$delete(rate, reverse);
        }
      });
    },
  },
};
</script>
